import Vue from 'vue'
import dayjs from 'dayjs'
import { DayjsUnit } from '@/helpers/DateService'
import {
  defineSyncSchema,
  schemaKinds,
  createSyncState,
  createSyncRequest,
  collectProtected,
  recordWrite,
  mergeRows,
  syncMutations
} from '@/helpers/storeSync'

import api from '@/api'

const DIAS_DEFAULT = 7

const PLANIFICACION_SYNC_SCHEMA = defineSyncSchema({
  key: 'idRecurso',
  kind: null,
  insertAt: 'end',
  children: {
    ciclos: { key: 'idRecursoSector', kind: 'ciclo', insertAt: 'end' }
  }
})

const initialState = {
  planificaciones: [],
  meta: { totalRecursos: 0, filtros: {} },
  loading: false,
  error: null,
  filtros: {
    idEmergencia: null,
    idRecursoTipo: null,
  },
  viewport: {
    escala: 'MIN_10',
    dias: DIAS_DEFAULT,
    rangoPersonalizado: null, // { inicio: 'YYYY-MM-DD', fin: 'YYYY-MM-DD' } | null
  },
  // Internal fetch token for anti-race-condition
  fetchToken: 0,
  sync: createSyncState(schemaKinds(PLANIFICACION_SYNC_SCHEMA))
}

const getters = {
  planificaciones: (state) => state.planificaciones,

  loading: (state) => state.loading,

  error: (state) => state.error,

  filtros: (state) => state.filtros,

  viewport: (state) => state.viewport,

  rangoTotal: (state) => {
    if (state.viewport.rangoPersonalizado) {
      const { inicio, fin } = state.viewport.rangoPersonalizado
      return {
        inicio: dayjs(inicio).startOf(DayjsUnit.Day),
        fin: dayjs(fin).endOf(DayjsUnit.Day),
      }
    }
    const dias = state.viewport.dias
    const half = Math.floor(dias / 2)
    return {
      inicio: dayjs().subtract(half, DayjsUnit.Day).startOf(DayjsUnit.Day),
      fin: dayjs().add(dias - half - 1, DayjsUnit.Day).endOf(DayjsUnit.Day),
    }
  },

  getCicloByIdRecursoSector: (state) => (idRecursoSector) => {
    for (const recurso of state.planificaciones) {
      const ciclo = recurso.ciclos.find(c => c.idRecursoSector === idRecursoSector)
      if (ciclo) return ciclo
    }
    return null
  },

  getRecursoById: (state) => (idRecurso) => {
    return state.planificaciones.find(r => r.idRecurso === idRecurso) || null
  },

  numCiclosTotal: (state) => {
    return state.planificaciones.reduce((total, recurso) => total + (recurso.ciclos ? recurso.ciclos.length : 0), 0)
  }
}

const mutations = {
  ...syncMutations,

  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  MERGE_GANTT(state, { data, meta, request }) {
    const protectedByKind = collectProtected(state.sync.stamps, request)
    state.planificaciones = mergeRows(state.planificaciones, data, PLANIFICACION_SYNC_SCHEMA, protectedByKind)
    state.meta = meta
  },

  SET_FILTROS(state, filtrosParciales) {
    state.filtros = { ...state.filtros, ...filtrosParciales }
  },

  SET_ESCALA(state, escala) {
    state.viewport.escala = escala
  },

  SET_DIAS(state, dias) {
    state.viewport.dias = dias
  },

  SET_RANGO_PERSONALIZADO(state, rango) {
    Vue.set(state.viewport, 'rangoPersonalizado', rango)
  },

  SET_FETCH_TOKEN(state, token) {
    state.fetchToken = token
  },

  SOCKET_addPlanificacion(state, payload) {
    const recurso = state.planificaciones.find(r => r.idRecurso === payload.idRecurso)

    // Ignore event if the resource is not in the current viewport (filtered out)
    if (!recurso) return

    Vue.set(recurso, 'ciclos', [...recurso.ciclos, payload.ciclo])
    recordWrite(state.sync, 'ciclo', payload.ciclo.idRecursoSector)
  },

  SOCKET_updatePlanificacion(state, payload) {
    const recurso = state.planificaciones.find(r => r.idRecurso === payload.idRecurso)

    if (!recurso) return

    const cicloIndex = recurso.ciclos.findIndex(c => c.idRecursoSector === payload.ciclo.idRecursoSector)

    if (cicloIndex !== -1) {
      const ciclosActualizados = [...recurso.ciclos]
      ciclosActualizados[cicloIndex] = payload.ciclo
      Vue.set(recurso, 'ciclos', ciclosActualizados)
    } else {
      Vue.set(recurso, 'ciclos', [...recurso.ciclos, payload.ciclo])
    }

    recordWrite(state.sync, 'ciclo', payload.ciclo.idRecursoSector)
  },

  SOCKET_deletePlanificacion(state, payload) {
    const recurso = state.planificaciones.find(r => r.idRecurso === payload.idRecurso)

    if (!recurso) return

    Vue.set(recurso, 'ciclos', recurso.ciclos.filter(c => c.idRecursoSector !== payload.idRecursoSector))
    recordWrite(state.sync, 'ciclo', payload.idRecursoSector, true)
  }
}

const actions = {
  async fetchGantt({ commit, state }) {
    const currentToken = state.fetchToken + 1
    commit('SET_FETCH_TOKEN', currentToken)
    commit('BEGIN_SYNC_REQUEST')
    const request = createSyncRequest(state.sync.seq)
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await api.planificacion.getGantt(state.filtros)

      if (state.fetchToken === currentToken) {
        commit('MERGE_GANTT', { data: response.data.data, meta: response.data.meta, request })
      }
    } catch (error) {
      console.error('[planificacion] fetchGantt', error)
      if (state.fetchToken === currentToken) {
        commit('SET_ERROR', error.message)
      }
    } finally {
      if (state.fetchToken === currentToken) {
        commit('SET_LOADING', false)
      }
    }
  },

  setFiltros({ commit, dispatch }, filtrosParciales) {
    commit('SET_FILTROS', filtrosParciales)
    dispatch('fetchGantt')
  },

  limpiarFiltros({ commit, dispatch }) {
    commit('SET_FILTROS', {
      idEmergencia: null,
      idRecursoTipo: null,
    })
    dispatch('fetchGantt')
  },

  setEscala({ commit }, escala) {
    commit('SET_ESCALA', escala)
  },

  setDias({ commit }, dias) {
    commit('SET_DIAS', dias)
  },

  setRangoPersonalizado({ commit }, rango) {
    commit('SET_RANGO_PERSONALIZADO', rango)
  },

  crearPlanificacion(_context, dto) {
    return api.planificacion.create(dto)
  },

  actualizarPlanificacion(_context, { id, dto }) {
    return api.planificacion.update(id, dto)
  },

  eliminarPlanificacion(_context, id) {
    return api.planificacion.delete(id)
  }
}

const module = {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions
}

export default module
